import type { ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircleExclamation,
  faCircleInfo,
  faCircleXmark,
  faHourglassHalf,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { cn } from '@/lib/cn';

export type CustomerBannerTone = 'info' | 'warning' | 'success' | 'danger' | 'pending';

interface CustomerStatusBannerProps {
  tone?: CustomerBannerTone;
  title: string;
  message?: ReactNode;
  icon?: IconDefinition;
  /** Optional action rendered under the text on phones, inline on wider screens. */
  action?: ReactNode;
  className?: string;
  'data-testid'?: string;
}

const TONE: Record<CustomerBannerTone, { box: string; icon: string; def: IconDefinition }> = {
  info: {
    box: 'border-customer-primary/25 bg-customer-primary/[0.06]',
    icon: 'bg-customer-primary/15 text-customer-primary-text',
    def: faCircleInfo,
  },
  warning: {
    box: 'border-customer-warning/35 bg-customer-warning/[0.08]',
    icon: 'bg-customer-warning/20 text-customer-warning-text',
    def: faCircleExclamation,
  },
  success: {
    box: 'border-customer-success/30 bg-customer-success/[0.08]',
    icon: 'bg-customer-success/20 text-customer-success-text',
    def: faCircleCheck,
  },
  danger: {
    box: 'border-customer-danger/30 bg-customer-danger/[0.06]',
    icon: 'bg-customer-danger/15 text-customer-danger-text',
    def: faCircleXmark,
  },
  pending: {
    box: 'border-customer-teal/30 bg-customer-teal/[0.08]',
    icon: 'bg-customer-teal/20 text-customer-teal-text',
    def: faHourglassHalf,
  },
};

/**
 * The "where am I / what is expected of me" strip. One glance answers the
 * two questions a customer opens a job for: current state and required
 * action. Icon + title + short message; never a wall of text.
 */
export function CustomerStatusBanner({
  tone = 'info',
  title,
  message,
  icon,
  action,
  className = '',
  'data-testid': testId,
}: CustomerStatusBannerProps) {
  const t = TONE[tone];
  return (
    <div
      role="status"
      data-testid={testId}
      className={cn(
        'flex flex-col gap-3 rounded-customer border p-4 sm:flex-row sm:items-center sm:gap-4',
        t.box,
        className,
      )}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <span
          className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-full', t.icon)}
          aria-hidden="true"
        >
          <FontAwesomeIcon icon={icon ?? t.def} className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold leading-5 text-customer-text">{title}</p>
          {/* D-130 Phase 6R — full-strength text, not the secondary tone. This
              paragraph sits on a TONE-TINTED box rather than the plain surface,
              and the tint costs just enough contrast to put the secondary tone
              at 4.50:1 in Forge light. The hierarchy is carried by the title's
              weight instead, which costs no legibility. */}
          {message && <p className="mt-0.5 text-sm leading-5 text-customer-text">{message}</p>}
        </div>
      </div>
      {action && <div className="shrink-0 sm:pl-2">{action}</div>}
    </div>
  );
}
